import {
    Column,
    convertFromBoolean,
    convertToBoolean,
    Form,
    FormDate,
    FormDropDown,
    FormInput,
    FormRadio,
    Row,
    SecondaryButton,
    useAlignments,
    useSizes,
} from 'metrix-common3';
// biome-ignore lint/correctness/noUnusedImports: React is required for JSX transformation
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FROZEN_FIELDS, isFieldFrozen } from '../../../../constants/policy/editScope';

const ProfileSecureMyOfferOfferInfo = (props) => {
    const _alignments = useAlignments();
    const _sizes = useSizes();
    const intl = useIntl();

    // State for conditional rendering
    const [showBuyerSellerRelationship, setShowBuyerSellerRelationship] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional - mimics componentDidMount behavior
    useEffect(() => {
        props.doGetPolicy();

        const pageHasTooltipInfo =
            props.tooltipInfo?.pages?.length && props.tooltipInfo.pages.some((p) => p.code === props.pageCode);
        if (!pageHasTooltipInfo) {
            props.doGetTooltipInfo(props.policy.product);
        }
    }, []);

    // Watch for buyer-seller relationship changes
    useEffect(() => {
        const isRelatedToBuyer = props.policy?.secureMyOfferCustomInfo?.isRelatedToBuyer;
        setShowBuyerSellerRelationship(isRelatedToBuyer === true);
    }, [props.policy?.secureMyOfferCustomInfo?.isRelatedToBuyer]);

    // REMOVED: showHomeValueEstimateOverride - OPTA override fields have been hidden since they don't affect premium.
    // Seller now uses MaxExpectedSaleAmount for premium calculation. Only PreApprovalRangeMaxAmountOverride affects premium.

    // Get translated text for the button
    const isResidentialValuationButtonAvailable = () => {
        // Only show if form is editable and user is admin or broker
        if (!props.allowEdit) {
            return false;
        }

        const userType = props.context?.session?.userType?.toLowerCase();
        return userType === 'administrator' || userType === 'broker';
    };

    // Check if GetOptaEstimator button should be shown
    const isEstimatorButtonAvailable = () => {
        // Only show if form is editable and user is admin or broker
        if (!props.allowEdit) {
            return false;
        }

        const userType = props.context?.session?.userType?.toLowerCase();
        return userType === 'administrator' || userType === 'broker';
    };

    return (
        <>
            <Form
                pageCode={props.pageCode}
                sectionCode='propertyInformation'
                saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                tooltipInfo={props.tooltipInfo}
                validationInfo={props.validationInfo}
                header={<FormattedMessage id='SecureMyOffer.section.propertyInformation' />}
                saveEvent={props.doUpsertPolicy}
                cancelEvent={props.doGetPolicy}
                allowEdit={props.allowEdit}
            >
                <FormRadio
                    fieldCode='isRuralArea'
                    label={<FormattedMessage id='SecureMyOffer.field.isRuralArea' />}
                    value={{ code: convertFromBoolean(props.policy?.secureMyOfferCustomInfo?.isRuralArea) }}
                    references={props.reference.references?.YesNo}
                    referencesEvent={() => props.reference.doGetReferences('YesNo')}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.isRuralArea',
                            value: convertToBoolean(value.code),
                        })
                    }
                />
                <FormDropDown
                    fieldCode='saleStatus'
                    label={<FormattedMessage id='SecureMyOffer.field.saleStatus' />}
                    value={{ code: props.policy?.secureMyOfferCustomInfo?.propertySaleStatusCode }}
                    references={props.reference?.references?.SecureMyOffer?.SaleStatuses}
                    referencesEvent={() => props.reference.doGetReferences('SecureMyOffer.SaleStatuses')}
                    changeEvent={(value) => {
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.propertySaleStatusCode',
                            value: value.code,
                        });
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.propertySaleStatusName',
                            value: value.value,
                        });
                    }}
                    allowEdit={!isFieldFrozen(props.editScope, FROZEN_FIELDS.PROPERTY_SALE_STATUS)}
                />
                <FormInput
                    fieldCode='saleAmount'
                    label={<FormattedMessage id='SecureMyOffer.field.saleAmount' />}
                    value={props.policy?.secureMyOfferCustomInfo?.saleAmount}
                    type='currency'
                    changeEvent={(value) =>
                        props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.saleAmount', value })
                    }
                    maxValue={9999999}
                    allowEdit={!isFieldFrozen(props.editScope, FROZEN_FIELDS.SALE_AMOUNT)}
                />
            </Form>
            <Form
                pageCode={props.pageCode}
                sectionCode='insuranceQuestions'
                saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                tooltipInfo={props.tooltipInfo}
                validationInfo={props.validationInfo}
                header={<FormattedMessage id='SecureMyOffer.section.insuranceQuestions' />}
                saveEvent={props.doUpsertPolicy}
                cancelEvent={props.doGetPolicy}
                allowEdit={props.allowEdit}
            >
                <FormRadio
                    fieldCode='isPrimaryResidence'
                    label={<FormattedMessage id='SecureMyOffer.field.isPrimaryResidence' />}
                    value={{ code: convertFromBoolean(props.policy?.secureMyOfferCustomInfo?.isPrimaryResidence) }}
                    references={props.reference.references?.YesNo}
                    referencesEvent={() => props.reference.doGetReferences('YesNo')}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.isPrimaryResidence',
                            value: convertToBoolean(value.code),
                        })
                    }
                    allowEdit
                />
                <FormRadio
                    fieldCode='isCondo'
                    label={<FormattedMessage id='SecureMyOffer.field.isCondo' />}
                    value={{ code: convertFromBoolean(props.policy?.secureMyOfferCustomInfo?.isCondo) }}
                    references={props.reference.references?.YesNo}
                    referencesEvent={() => props.reference.doGetReferences('YesNo')}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.isCondo',
                            value: convertToBoolean(value.code),
                        })
                    }
                    allowEdit
                />
                <FormRadio
                    fieldCode='allOwnersAreIndividuals'
                    label={<FormattedMessage id='SecureMyOffer.field.allOwnersAreIndividuals' />}
                    value={{ code: convertFromBoolean(props.policy?.secureMyOfferCustomInfo?.allOwnersAreIndividuals) }}
                    references={props.reference.references?.YesNo}
                    referencesEvent={() => props.reference.doGetReferences('YesNo')}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.allOwnersAreIndividuals',
                            value: convertToBoolean(value.code),
                        })
                    }
                    allowEdit
                />
                <FormRadio
                    fieldCode='isNewConstruction'
                    label={<FormattedMessage id='SecureMyOffer.field.isNewConstruction' />}
                    value={{ code: convertFromBoolean(props.policy?.secureMyOfferCustomInfo?.isNewConstruction) }}
                    references={props.reference.references?.YesNo}
                    referencesEvent={() => props.reference.doGetReferences('YesNo')}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.isNewConstruction',
                            value: convertToBoolean(value.code),
                        })
                    }
                    allowEdit
                />
                <FormDate
                    fieldCode='currentPropertySaleGoFirmDate'
                    label={<FormattedMessage id='SecureMyOffer.field.currentPropertySaleGoFirmDate' />}
                    value={props.policy?.secureMyOfferCustomInfo?.currentPropertySaleGoFirmDate}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.currentPropertySaleGoFirmDate',
                            value,
                        })
                    }
                    culture={props.context.session.culture}
                    mode='past'
                    take={11}
                    skip={-6}
                    allowEdit
                />
                <FormInput
                    fieldCode='securedDepositAmount'
                    label={<FormattedMessage id='SecureMyOffer.field.depositAmount' />}
                    value={props.policy?.secureMyOfferCustomInfo?.securedDepositAmount}
                    type='currency'
                    changeEvent={(value) =>
                        props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.securedDepositAmount', value })
                    }
                    allowEdit
                />
                <FormRadio
                    fieldCode='depositFormCode'
                    label={<FormattedMessage id='SecureMyOffer.field.depositForm' />}
                    value={{ code: props.policy?.secureMyOfferCustomInfo?.depositFormCode }}
                    references={props.reference?.references?.SecureMyOffer?.DepositForm}
                    referencesEvent={() => props.reference.doGetReferences('SecureMyOffer.DepositForm')}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.depositFormCode',
                            value: value.code,
                        })
                    }
                    allowEdit
                />
                <FormDate
                    fieldCode='currentPropertySaleClosingDate'
                    label={<FormattedMessage id='SecureMyOffer.field.currentPropertySaleClosingDate' />}
                    value={props.policy?.secureMyOfferCustomInfo?.currentPropertySaleClosingDate}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.currentPropertySaleClosingDate',
                            value,
                        })
                    }
                    culture={props.context.session.culture}
                    mode='past'
                    take={11}
                    skip={-6}
                    allowEdit
                />
                <FormRadio
                    fieldCode='hasPropertyInsuranceWithVacancy'
                    label={<FormattedMessage id='SecureMyOffer.field.hasPropertyInsuranceWithVacancy' />}
                    value={{
                        code: convertFromBoolean(
                            props.policy?.secureMyOfferCustomInfo?.hasPropertyInsuranceWithVacancy,
                        ),
                    }}
                    references={props.reference.references?.YesNo}
                    referencesEvent={() => props.reference.doGetReferences('YesNo')}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.hasPropertyInsuranceWithVacancy',
                            value: convertToBoolean(value.code),
                        })
                    }
                    allowEdit
                />
                <FormRadio
                    fieldCode='bothPartiesRepresented'
                    label={<FormattedMessage id='SecureMyOffer.field.bothPartiesRepresented' />}
                    value={{ code: convertFromBoolean(props.policy?.secureMyOfferCustomInfo?.bothPartiesRepresented) }}
                    references={props.reference.references?.YesNo}
                    referencesEvent={() => props.reference.doGetReferences('YesNo')}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.bothPartiesRepresented',
                            value: convertToBoolean(value.code),
                        })
                    }
                    allowEdit
                />
                <FormRadio
                    fieldCode='isRelatedToBuyer'
                    label={<FormattedMessage id='SecureMyOffer.field.isRelatedToBuyer' />}
                    value={{ code: convertFromBoolean(props.policy?.secureMyOfferCustomInfo?.isRelatedToBuyer) }}
                    references={props.reference.references?.YesNo}
                    referencesEvent={() => props.reference.doGetReferences('YesNo')}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.isRelatedToBuyer',
                            value: convertToBoolean(value.code),
                        })
                    }
                    allowEdit
                />
                {showBuyerSellerRelationship && (
                    <FormInput
                        fieldCode='buyerSellerRelationshipDescription'
                        label={<FormattedMessage id='SecureMyOffer.field.buyerSellerRelationship' />}
                        value={props.policy?.secureMyOfferCustomInfo?.buyerSellerRelationshipDescription}
                        type='text'
                        changeEvent={(value) =>
                            props.doReducePolicyElement({
                                code: 'secureMyOfferCustomInfo.buyerSellerRelationshipDescription',
                                value,
                            })
                        }
                        allowEdit
                    />
                )}
                <FormRadio
                    fieldCode='uncertainBuyer'
                    label={<FormattedMessage id='SecureMyOffer.field.uncertainBuyer' />}
                    value={{ code: convertFromBoolean(props.policy?.secureMyOfferCustomInfo?.uncertainBuyer) }}
                    references={props.reference.references?.YesNo}
                    referencesEvent={() => props.reference.doGetReferences('YesNo')}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.uncertainBuyer',
                            value: convertToBoolean(value.code),
                        })
                    }
                    allowEdit
                />
                <FormRadio
                    fieldCode='hasPartnerMembership'
                    label={<FormattedMessage id='SecureMyOffer.field.hasPartnerMembership' />}
                    value={{ code: convertFromBoolean(props.policy?.secureMyOfferCustomInfo?.hasPartnerMembership) }}
                    references={props.reference.references?.YesNo}
                    referencesEvent={() => props.reference.doGetReferences('YesNo')}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.hasPartnerMembership',
                            value: convertToBoolean(value.code),
                        })
                    }
                    allowEdit
                />
            </Form>
            <Form
                pageCode={props.pageCode}
                sectionCode='requestedCoverage'
                saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                tooltipInfo={props.tooltipInfo}
                validationInfo={props.validationInfo}
                header={<FormattedMessage id='SecureMyOffer.section.requestedCoverage' />}
                saveEvent={props.doUpsertPolicy}
                cancelEvent={props.doGetPolicy}
                allowEdit={props.allowEdit}
            >
                <FormInput
                    fieldCode='coverageCodeSelected'
                    label={<FormattedMessage id='SecureMyOffer.field.coverageCodeSelected' />}
                    value={props.policy?.secureMyOfferCustomInfo?.coverageCodeSelected}
                    type='text'
                    changeEvent={(value) =>
                        props.doReducePolicyElement({ code: 'secureMyOfferCustomInfo.coverageCodeSelected', value })
                    }
                />
                <FormRadio
                    fieldCode='isBnplProgram'
                    label={<FormattedMessage id='SecureMyOffer.field.isBnplProgram' />}
                    value={{ code: convertFromBoolean(props.policy?.secureMyOfferCustomInfo?.isBnplProgram) }}
                    references={props.reference.references?.YesNo}
                    referencesEvent={() => props.reference.doGetReferences('YesNo')}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'secureMyOfferCustomInfo.isBnplProgram',
                            value: convertToBoolean(value.code),
                        })
                    }
                    allowEdit={false}
                />
            </Form>
            <Form
                pageCode={props.pageCode}
                sectionCode='residentialMarketValuation'
                saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                tooltipInfo={props.tooltipInfo}
                validationInfo={props.validationInfo}
                header={<FormattedMessage id='SecureMyOffer.section.residentialMarketValuation' />}
                allowEdit={false}
            >
                <FormInput
                    fieldCode='residentialMarketValuationConfidenceRating'
                    label={<FormattedMessage id='SecureMyOffer.field.residentialMarketValuationConfidenceRating' />}
                    value={props.policy.secureMyOfferCustomInfo.residentialMarketValuationConfidenceRating}
                    type='text'
                    allowEdit={false}
                />
                <FormInput
                    fieldCode='residentialMarketValuationRangeMinimumValue'
                    label={<FormattedMessage id='SecureMyOffer.field.lowestValuationRange' />}
                    value={props.policy.secureMyOfferCustomInfo.residentialMarketValuationRangeMinimumValue}
                    type='currency'
                />
                <FormInput
                    fieldCode='residentialMarketValuationRangeMaximumValue'
                    label={<FormattedMessage id='SecureMyOffer.field.highestValuationRange' />}
                    value={props.policy.secureMyOfferCustomInfo.residentialMarketValuationRangeMaximumValue}
                    type='currency'
                />
                <FormInput
                    fieldCode='residentialMarketValuationValue'
                    label={<FormattedMessage id='SecureMyOffer.field.actualValuation' />}
                    value={props.policy.secureMyOfferCustomInfo.residentialMarketValuationValue}
                    type='currency'
                />
                <FormDate
                    fieldCode='residentialMarketValuationDate'
                    label={<FormattedMessage id='SecureMyOffer.field.valuationDate' />}
                    value={props.policy?.secureMyOfferCustomInfo.residentialMarketValuationDate}
                    culture={props.context.session.culture}
                />
                {isResidentialValuationButtonAvailable() && (
                    <Row>
                        <Column xs={12}>
                            <SecondaryButton
                                text={intl.formatMessage({ id: 'SecureMyOffer.button.getValuation' })}
                                clickEvent={() =>
                                    props.doGetOpta({
                                        errorMessage: intl.formatMessage({ id: 'SecureMyOffer.error.getValuation' }),
                                    })
                                }
                            />
                        </Column>
                    </Row>
                )}
            </Form>

            {/* Home Value Estimate Form Section.
                All valuation fields are readonly. Override fields have been hidden since they don't
                affect premium calculation (Seller now uses MaxExpectedSaleAmount for premium). */}
            <Form
                pageCode={props.pageCode}
                sectionCode='optaEstimator'
                saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                tooltipInfo={props.tooltipInfo}
                validationInfo={props.validationInfo}
                header={<FormattedMessage id='SecureMyOffer.section.homeValueEstimate' />}
                allowEdit={false}
            >
                <FormInput
                    fieldCode='optaEstimatorValuationRangeMinimumValue'
                    label={<FormattedMessage id='SecureMyOffer.field.lowestValuationRange' />}
                    value={props.policy.secureMyOfferCustomInfo.optaEstimatorValuationRangeMinimumValue}
                    type='currency'
                    allowEdit={false}
                />
                <FormInput
                    fieldCode='optaEstimatorValuationRangeMaximumValue'
                    label={<FormattedMessage id='SecureMyOffer.field.highestValuationRange' />}
                    value={props.policy.secureMyOfferCustomInfo.optaEstimatorValuationRangeMaximumValue}
                    type='currency'
                    allowEdit={false}
                />
                <FormInput
                    fieldCode='optaEstimatorValuationValue'
                    label={<FormattedMessage id='SecureMyOffer.field.actualValuation' />}
                    value={props.policy.secureMyOfferCustomInfo.optaEstimatorValuationValue}
                    type='currency'
                    allowEdit={false}
                />
                <FormDate
                    fieldCode='optaEstimatorValuationDate'
                    label={<FormattedMessage id='SecureMyOffer.field.valuationDate' />}
                    value={props.policy?.secureMyOfferCustomInfo.optaEstimatorValuationDate}
                    culture={props.context.session.culture}
                    allowEdit={false}
                />

                {/* HIDDEN: OPTA valuation override fields - NOT used for premium calculation.
                    Seller now uses MaxExpectedSaleAmount (same as RealtorLawyer) for premium.
                    OPTA is used only for validation (sanity band check).
                    Only PreApprovalRangeMaxAmountOverride affects premium calculation.

                    Hidden fields:
                    - lowestValuationRangeOverride
                    - highestValuationRangeOverride
                    - actualValuationOverride
                */}

                {isEstimatorButtonAvailable() && (
                    <Row>
                        <Column xs={12}>
                            <SecondaryButton
                                text={intl.formatMessage({ id: 'SecureMyOffer.button.getEstimatedHomeValue' })}
                                clickEvent={() =>
                                    props.doGetOptaEstimator({
                                        errorMessage: intl.formatMessage({
                                            id: 'SecureMyOffer.error.getEstimatedHomeValue',
                                        }),
                                    })
                                }
                            />
                        </Column>
                    </Row>
                )}
            </Form>
        </>
    );
};

export default ProfileSecureMyOfferOfferInfo;
